import { useNavigate } from "react-router-dom";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Switch,
  TimePicker,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { apiUrlV2 } from "@/consts";
import { wallClockISO } from "./time";

const { Title, Text } = Typography;

// Admin-authored Strength & Conditioning (GL-000). The platform owns the global S&C
// schedule; clubs only pick a preferred day. A session targets one or more
// PERFORMANCE_GROUPs (not a team) — groups that train together share one class
// rather than needing a duplicate schedule each — and supports the same one-off /
// recurring engine as club sessions.

const PERFORMANCE_GROUPS = [
  { value: "academy", label: "Academy" },
  { value: "foundation", label: "Foundation" },
  { value: "development", label: "Development" },
  { value: "performance", label: "Performance" },
];

const WEEKDAYS = [
  { value: "MON", label: "Mon" },
  { value: "TUE", label: "Tue" },
  { value: "WED", label: "Wed" },
  { value: "THU", label: "Thu" },
  { value: "FRI", label: "Fri" },
  { value: "SAT", label: "Sat" },
  { value: "SUN", label: "Sun" },
];

export function ScScheduleCreate() {
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const [form] = Form.useForm();
  const mode = Form.useWatch("mode", form) ?? "recurring";
  const freq = Form.useWatch("freq", form) ?? "WEEKLY";
  const endsType = Form.useWatch("endsType", form) ?? "AFTER_WEEKS";

  // S&C is platform-authored: the admin sets the time per session (the session-type
  // FIXED-time policy is bypassed on the admin path), so times are always collected.
  const { mutate, isLoading } = useCustomMutation();

  function handleFinish(values: any) {
    const base: Record<string, unknown> = {
      type: "STRENGTH_CONDITIONING",
      title: values.title?.trim() || undefined,
      notes: values.notes?.trim() || undefined,
      target: { targetType: "PERFORMANCE_GROUP", targetIds: values.groups },
      postCheckinEnabled: !!values.postCheckinEnabled,
    };

    let payload: Record<string, unknown>;
    if (values.mode === "one_off") {
      payload = {
        ...base,
        startsAt: wallClockISO(values.startsAt as dayjs.Dayjs),
        endsAt: wallClockISO(values.endsAt as dayjs.Dayjs),
      };
    } else {
      const recurrence: Record<string, unknown> = {
        freq: values.freq,
        startDate: (values.startDate as dayjs.Dayjs).format("YYYY-MM-DD"),
        endsType: values.endsType,
        timeOfDay: (values.timeOfDay as dayjs.Dayjs).format("HH:mm"),
        endTimeOfDay: (values.endTimeOfDay as dayjs.Dayjs).format("HH:mm"),
      };
      if (values.freq === "WEEKLY" || values.freq === "BIWEEKLY") {
        recurrence.byDays = values.byDays;
      }
      if (values.endsType === "ON_DATE") {
        recurrence.endsOn = (values.endsOn as dayjs.Dayjs).format("YYYY-MM-DD");
      }
      if (values.endsType === "AFTER_WEEKS") {
        recurrence.endsAfterWeeks = values.endsAfterWeeks;
      }
      payload = { ...base, recurrence };
    }

    mutate(
      { url: `${apiUrlV2}/schedule/admin/sc`, method: "post", values: payload },
      {
        onSuccess: async () => {
          message.success("S&C session created");
          await invalidate({ invalidates: ["all"] });
          navigate("/sc-schedule");
        },
        onError: (err: any) => {
          message.error(err?.response?.data?.error || err?.message || "Could not create session");
        },
      },
    );
  }

  return (
    <Card style={{ maxWidth: 720, margin: "0 auto" }}>
      <Title level={4}>Create S&amp;C session</Title>
      <Text type="secondary">
        Platform-wide Strength &amp; Conditioning for one or more performance groups.
        Every club's athletes in those groups see it automatically.
      </Text>

      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 24 }}
        initialValues={{ mode: "recurring", freq: "WEEKLY", endsType: "AFTER_WEEKS", postCheckinEnabled: false }}
        onFinish={handleFinish}
      >
        <Form.Item
          name="groups"
          label="Performance groups"
          rules={[{ required: true, message: "Pick at least one performance group" }]}
          tooltip="Athletes in ANY of the selected groups see this class and can check in to it."
        >
          <Select
            mode="multiple"
            options={PERFORMANCE_GROUPS}
            placeholder="Select one or more groups"
          />
        </Form.Item>

        <Form.Item name="title" label="Title (optional)">
          <Input placeholder="e.g. Foundation S&C — Lower Body" maxLength={255} />
        </Form.Item>

        <Form.Item name="mode" label="Type">
          <Radio.Group
            options={[
              { value: "recurring", label: "Recurring" },
              { value: "one_off", label: "One-off" },
            ]}
            optionType="button"
          />
        </Form.Item>

        {mode === "one_off" ? (
          <Space size="large" wrap>
            <Form.Item
              name="startsAt"
              label="Starts at"
              rules={[{ required: true, message: "Pick a start" }]}
            >
              <DatePicker showTime format="YYYY-MM-DD HH:mm" />
            </Form.Item>
            <Form.Item
              name="endsAt"
              label="Ends at"
              rules={[{ required: true, message: "Pick an end" }]}
            >
              <DatePicker showTime format="YYYY-MM-DD HH:mm" />
            </Form.Item>
          </Space>
        ) : (
          <>
            <Space size="large" wrap>
              <Form.Item name="freq" label="Frequency" rules={[{ required: true }]}>
                <Select
                  style={{ width: 160 }}
                  options={[
                    { value: "WEEKLY", label: "Weekly" },
                    { value: "BIWEEKLY", label: "Every 2 weeks" },
                    { value: "MONTHLY", label: "Monthly" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="startDate"
                label="Start date"
                rules={[{ required: true, message: "Pick a start date" }]}
              >
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>
            </Space>

            {(freq === "WEEKLY" || freq === "BIWEEKLY") && (
              <Form.Item
                name="byDays"
                label="On days"
                rules={[{ required: true, message: "Pick at least one day" }]}
              >
                <Select mode="multiple" options={WEEKDAYS} placeholder="Select days" />
              </Form.Item>
            )}

            <Space size="large" wrap>
              <Form.Item
                name="timeOfDay"
                label="Start time"
                rules={[{ required: true, message: "Pick a start time" }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
              <Form.Item
                name="endTimeOfDay"
                label="End time"
                rules={[{ required: true, message: "Pick an end time" }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
            </Space>

            <Form.Item name="endsType" label="Ends" rules={[{ required: true }]}>
              <Select
                style={{ width: 220 }}
                options={[
                  { value: "AFTER_WEEKS", label: "After N weeks" },
                  { value: "ON_DATE", label: "On a date" },
                  { value: "END_OF_SEASON", label: "End of season" },
                ]}
              />
            </Form.Item>

            {endsType === "AFTER_WEEKS" && (
              <Form.Item
                name="endsAfterWeeks"
                label="Number of weeks"
                rules={[{ required: true, message: "Enter the number of weeks" }]}
              >
                <InputNumber min={1} max={104} />
              </Form.Item>
            )}
            {endsType === "ON_DATE" && (
              <Form.Item
                name="endsOn"
                label="End date"
                rules={[{ required: true, message: "Pick an end date" }]}
              >
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>
            )}
          </>
        )}

        <Form.Item name="notes" label="Notes (optional)">
          <Input.TextArea rows={3} placeholder="Internal notes for this session" />
        </Form.Item>

        <Form.Item
          name="postCheckinEnabled"
          label="Collect session feedback (check-ins)"
          valuePropName="checked"
          tooltip="Athletes in this performance group can log attendance + feedback after the session."
        >
          <Switch />
        </Form.Item>

        <Space>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Create session
          </Button>
          <Button onClick={() => navigate("/sc-schedule")}>Cancel</Button>
        </Space>
      </Form>
    </Card>
  );
}
